import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkRemoveFromGroup } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface BulkRemoveFromGroupProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function BulkRemoveFromGroupDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: BulkRemoveFromGroupProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [groupTarget, setGroupTarget] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isRemoving } = useMutation({
    mutationFn: () =>
      bulkRemoveFromGroup('User', selectedUsers, groupTarget, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'The task to removed the user(s) from the selected group has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.Groups, user] });
      });
      setChangeLogMessage('');
      setGroupTarget('');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue removing the user(s) from this group. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_remove_from_group, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.BulkRemoveUsersFromGroup.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setGroupTarget('');
      }}
      title="Remove from Group"
      dialogContent={
        <>
          <Typography sx={{ mb: 2 }}>
            {selectedUsers.length > 1 ? (
              <>
                Remove <strong>{selectedUsers.length} users</strong> from group.
              </>
            ) : (
              <>
                Remove <strong>{selectedUsers[0]}</strong> from group.
              </>
            )}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <SharedAutocomplete
              autoFocus
              sources={['groups']}
              autocompleteValue={groupTarget}
              setAutoCompleteValue={setGroupTarget}
              label="Search for a group"
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isRemoving}
          />
        </>
      }
      primaryButton={{
        loading: isRemoving,
        action: handleSubmit,
        disabled: isRemoving || groupTarget.length === 0,
        title: 'Remove from Group',
        color: 'warning',
      }}
    />
  );
}
